import React, { useState, useCallback, useRef, useEffect, Suspense } from 'react';
import { Box, Button, Collapse, Stack, TextField, Typography, CircularProgress, Chip } from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

// Lazy-load Monaco to avoid 2MB bundle hit on initial load
const MonacoEditor = React.lazy(() =>
  import('@monaco-editor/react').then(mod => ({ default: mod.default })).catch(() => ({
    default: null as any,
  }))
);

interface AdvancedExpressionEditorProps {
  expression: string;
  onChange: (expression: string) => void;
  /** When true, the editor starts expanded */
  forceExpanded?: boolean;
  /** Available column names for autocomplete */
  availableColumns?: string[];
  /** Callback to validate expression server-side */
  onValidate?: (expression: string) => Promise<{ valid: boolean; error?: string }>;
}

const AdvancedExpressionEditor: React.FC<AdvancedExpressionEditorProps> = ({
  expression,
  onChange,
  forceExpanded,
  availableColumns,
  onValidate,
}) => {
  const [expanded, setExpanded] = useState(forceExpanded ?? false);
  const [validationStatus, setValidationStatus] = useState<'idle' | 'valid' | 'error'>('idle');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [monacoAvailable, setMonacoAvailable] = useState<boolean | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Check if Monaco is available
  useEffect(() => {
    import('@monaco-editor/react')
      .then(() => setMonacoAvailable(true))
      .catch(() => setMonacoAvailable(false));
  }, []);

  // Debounced validation
  const handleChange = useCallback(
    (value: string) => {
      onChange(value);
      setValidationStatus('idle');
      setValidationError(null);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (onValidate && value.trim()) {
        debounceRef.current = setTimeout(async () => {
          try {
            const result = await onValidate(value);
            setValidationStatus(result.valid ? 'valid' : 'error');
            setValidationError(result.error || null);
          } catch {
            // Validation endpoint not available — silently ignore
          }
        }, 500);
      }
    },
    [onChange, onValidate],
  );

  // Monaco editor options
  const monacoOptions = {
    minimap: { enabled: false },
    lineNumbers: 'off' as const,
    scrollBeyondLastLine: false,
    wordWrap: 'on' as const,
    fontSize: 13,
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
    tabSize: 2,
    automaticLayout: true,
    renderLineHighlight: 'none' as const,
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    scrollbar: { vertical: 'auto' as const, horizontal: 'auto' as const },
    padding: { top: 8, bottom: 8 },
  };

  // Configure Monaco on mount — register column name suggestions
  const handleEditorMount = useCallback(
    (editor: any, monaco: any) => {
      if (!availableColumns?.length || !monaco) return;
      monaco.languages.registerCompletionItemProvider('sql', {
        provideCompletionItems: (_model: any, position: any) => {
          const suggestions = (availableColumns || []).map((col) => ({
            label: col,
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: `"${col}"`,
            range: {
              startLineNumber: position.lineNumber,
              startColumn: position.column,
              endLineNumber: position.lineNumber,
              endColumn: position.column,
            },
          }));
          return { suggestions };
        },
      });
    },
    [availableColumns],
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Button
          size="small"
          variant="text"
          startIcon={expanded ? <VisibilityOffIcon fontSize="small" /> : <CodeIcon fontSize="small" />}
          onClick={() => setExpanded(!expanded)}
          sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary' }}
        >
          {expanded ? 'Hide Expression' : 'View Expression'}
        </Button>
        {validationStatus === 'valid' && (
          <Chip
            size="small"
            icon={<CheckCircleOutlineIcon />}
            label="Valid"
            color="success"
            variant="outlined"
            sx={{ height: 20, fontSize: 11 }}
          />
        )}
        {validationStatus === 'error' && (
          <Chip
            size="small"
            icon={<ErrorOutlineIcon />}
            label="Error"
            color="error"
            variant="outlined"
            sx={{ height: 20, fontSize: 11 }}
          />
        )}
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ mt: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          {monacoAvailable ? (
            <Suspense fallback={<Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>}>
              <MonacoEditor
                height="120px"
                language="sql"
                value={expression}
                onChange={(val: string | undefined) => handleChange(val ?? '')}
                options={monacoOptions}
                onMount={handleEditorMount}
                theme="vs-light"
              />
            </Suspense>
          ) : (
            <TextField
              multiline
              minRows={3}
              maxRows={8}
              value={expression}
              onChange={(e) => handleChange(e.target.value)}
              fullWidth
              size="small"
              placeholder="e.g. UPPER(TRIM(email))"
              InputProps={{
                sx: {
                  fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                  fontSize: '0.82rem',
                  lineHeight: 1.5,
                },
              }}
            />
          )}
        </Box>
        {validationError && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', fontFamily: 'monospace', fontSize: 11 }}>
            {validationError}
          </Typography>
        )}
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25, display: 'block' }}>
          {monacoAvailable
            ? 'SQL expression editor with autocomplete. Type column names for suggestions.'
            : 'Edit the raw expression directly. Changes here will update the visual builder if parseable.'}
        </Typography>
      </Collapse>
    </Box>
  );
};

export default AdvancedExpressionEditor;
